import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { cn } from "@/utils/utils";
import { AI, AlertTriangle, Insights } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { Button } from "@/components/ui/button";
import { map } from "lodash";

const TRANSLATION_PREFIX = "fromDreamer";
const dealSummary =
  "The deal is currently in the POC stage with an estimated close date of December 31st, 2026. The Decision Maker recently changed to Don Muse, IT director. Following a positive demo, the next step is signing an NDA, meeting to discuss the POC scope, and addressing any remaining questions, particularly regarding price, as competitor Globex Corp has presented a lower offer. While the overall sentiment is positive, securing the deal may involve navigating price sensitivity.";
const leadSummary =
  "Alnatura, a chain of organic food supermarkets and producer of organic food with an estimated 3,500 employees, emerged as a warm lead from an industry webinar on cloud computing, with Head of the Supply Chain Service Aaron Beyer expressing strong interest in our services due to challenges with their legacy systems. This indicates a potential need for cloud migration and infrastructure modernization, making the next crucial step scheduling a discovery call to delve into their specific requirements and timeline.";
const alerts: { type: "warning" | "error"; text: string }[] = [
  {
    type: "warning",
    text: "Agreeing on product utilization-based success criteria vs. Revenue ones will increase your POC conversion rate by 35%."
  }
];

interface Props {
  entityType: EntityType.DEAL | EntityType.LEAD;
  className?: string;
}

const FromDreamerForDemo: React.FC<Props> = ({ entityType, className }) => {
  const { t } = useTranslation();
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  return (
    <div
      className={cn(
        "h-fit flex flex-col gap-4 bg-surface-elevation1 border-[0.5px] border-border-border1 w-full p-4 rounded-lg",
        className
      )}
    >
      <div className="flex gap-2 items-center">
        <IconContainer size="5" color="cta">
          <AI />
        </IconContainer>
        <Text variant="lgSubtitleH1">{t(`${TRANSLATION_PREFIX}.title`)}</Text>
      </div>
      <div className="flex flex-col gap-[6px]">
        <Text variant="labelSecondary">
          {t(`${TRANSLATION_PREFIX}.summary`)}
        </Text>
        <div className="flex flex-col">
          <Text
            variant="body"
            className={cn(
              "max-w-full overflow-hidden text-ellipsis transition-all duration-300 ease-in-out",
              isSummaryExpanded ? "max-h-96" : "max-h-20 line-clamp-3"
            )}
          >
            {entityType === EntityType.DEAL ? dealSummary : leadSummary}
          </Text>
          <Button
            variant="tertiary"
            className="self-end"
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
          >
            {t(isSummaryExpanded ? "readLess" : "readMore")}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {entityType === EntityType.DEAL &&
          map(alerts, (alert) => (
            <div className="py-2 flex gap-3 items-center">
              <IconContainer
                size="5"
                style={{
                  color:
                    alert.type === "warning"
                      ? "var(--gold-200)"
                      : "var(--error)",
                  filter:
                    alert.type === "warning"
                      ? "drop-shadow(1px 1px 20px var(--gold-200))"
                      : "drop-shadow(1px 1px 20px var(--error))"
                }}
              >
                {alert.type === "warning" ? <Insights /> : <AlertTriangle />}
              </IconContainer>
              <Text variant="smBody">{alert.text}</Text>
            </div>
          ))}
      </div>
    </div>
  );
};

export default FromDreamerForDemo;
